import type { HomepageContent } from '../../cms/homepage'
import HomepageActionLink from './HomepageActionLink'

type HomepageHeroProps = {
  content: HomepageContent['hero']
  heroImage?: HomepageContent['heroImage']
}

const buildButtonClass = (variant?: 'primary' | 'secondary' | 'ghost') =>
  ['button', variant === 'primary' ? 'primary' : null, variant === 'ghost' ? 'ghost' : null]
    .filter(Boolean)
    .join(' ')

export default function HomepageHero({
  content,
  heroImage,
}: HomepageHeroProps) {
  const fallbackSrc = '/images/home/homepagebanner-1280.webp'
  const imageSrc = heroImage?.asset?.url ?? fallbackSrc
  const imageSrcSet = heroImage?.asset?.url
    ? undefined
    : '/images/home/homepagebanner-768.webp 768w, /images/home/homepagebanner-1280.webp 1280w, /images/home/homepagebanner-1920.webp 1920w'

  return (
    <section className="home-hero home-section">
      <div className="home-hero-grid">
        <div className="home-hero-copy">
          <div className="home-hero-copy-panel">
            <p className="eyebrow">{content.eyebrow}</p>
            <h1>{content.title}</h1>
            <p className="lead">{content.subtitle}</p>
            {content.supportingLine && <p className="home-hero-support">{content.supportingLine}</p>}
            <div className="button-row home-hero-actions">
              {content.ctas.map((cta) => (
                <HomepageActionLink
                  key={`${cta.label}-${cta.href}`}
                  className={buildButtonClass(cta.variant)}
                  href={cta.href}
                >
                  {cta.label}
                </HomepageActionLink>
              ))}
            </div>
          </div>
        </div>
        <figure className="home-hero-visual">
          <img
            src={imageSrc}
            srcSet={imageSrcSet}
            sizes="(max-width: 959px) 100vw, 60vw"
            alt={heroImage?.alt ?? 'Places in Time collage showcasing the atlas design system'}
            loading="eager"
            fetchPriority="high"
            width={1920}
            height={1080}
            decoding="async"
          />
          <figcaption className="home-hero-visual-meta">
            <p className="home-hero-visual-label">What you can do with Places in Time</p>
            <ul className="home-hero-highlight-list">
              {content.productHighlights.map((item) => (
                <li key={item.label}>
                  <HomepageActionLink className="home-hero-highlight-link" href={item.href}>
                    {item.label}
                  </HomepageActionLink>
                </li>
              ))}
            </ul>
            <p className="home-hero-visual-note">{content.visualCaption}</p>
          </figcaption>
        </figure>
      </div>
    </section>
  )
}
